import { Fragment, FunctionComponent } from "react";
import styled from "@emotion/styled";
import {
    getMonths,
    getDays,
    getHours,
    getMinutes,
    getSeconds,
} from "./timeFormatters";
import CounterItem from "./CounterItem";
import { useCurrentTimeForAnimation } from "@mediaspace/hooks";
import { translate } from "@mediaspace/shared/utils";

export interface CounterProps {
    className?: string;
    timestamp: number;
    hideOnPastTime?: boolean;
    textWhenEnds?: string;
}

const StyledCounter = styled("span")(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    marginBottom: theme.spacing(3),
    color: theme.kaltura.palette.translucent.main,
    textTransform: "uppercase",
    [theme.breakpoints.only("xs")]: {
        marginBottom: theme.spacing(2),
    },
}));

const StyledCounterItem = styled(CounterItem)(
    ({ theme }) => ({
        "&:not(:first-of-type)": {
            marginLeft: theme.spacing(1),
        },
    })
);

/**
 * counter component
 * counting time until session start, according to scheduling data
 */
const Counter: FunctionComponent<CounterProps> = ({ timestamp, ...props }) => {
    const now = useCurrentTimeForAnimation(undefined, 1000);
    let remainingTime = Math.round((timestamp - now) / 1000);
    if (remainingTime < 0) {
        remainingTime = 0;
    }

    return (
        <CounterByTime
            className={"chromatic-ignore"}
            remainingTime={remainingTime}
            {...props}
        />
    );
};

export interface CounterByTimeProps extends Omit<CounterProps, "timestamp"> {
    className?: string;
    remainingTime: number;
}

export const CounterByTime = (props: CounterByTimeProps) => {
    const {
        className,
        remainingTime,
        textWhenEnds,
    } = props;

    const remainingSeconds = getSeconds(remainingTime);
    const remainingMinutes = getMinutes(remainingTime);
    const remainingHours = getHours(remainingTime);
    const remainingDays = getDays(remainingTime);
    const remainingMonths = getMonths(remainingTime);

    let parts = [
        {
            value: remainingDays,
            label: Number(remainingDays) !== 1 ? translate("DAYS") : translate("DAY"),
        },
        { value: remainingHours, label: translate("HRS") },
        { value: remainingMinutes, label: translate("MIN") },
        { value: remainingSeconds, label: translate("SEC") },
    ];
    if (remainingMonths > 0) {
        parts.unshift({
            value: Math.min(99, remainingMonths).toString(),
            label: remainingMonths !== 1 ? translate("MONTHS") : translate("MONTH"),
        });
    }

    // Cut leading zeros, but leave at least 2 units
    while (Number(parts[0].value) === 0 && parts.length > 2) {
        parts.shift();
    }

    // Leave only 2 leading units
    parts = parts.slice(0, 2);

    return (
        <StyledCounter className={className}>
            {remainingTime <= 0 && (
                <StyledCounterItem text={textWhenEnds} />
            )}
            {remainingTime > 0 ? (
                <>
                    {parts.map(({ label, value }, index) => (
                        <Fragment key={label}>
                            {index > 0 && (
                                <StyledCounterItem
                                    className={`${className}-item`}
                                    text={":"}
                                />
                            )}

                            <StyledCounterItem
                                className={`${className}-item`}
                                text={`${value} ${label}`}
                            />
                        </Fragment>
                    ))}
                </>
            ) : null}
        </StyledCounter>
    );
};

export default Counter;
